import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ComingSoonBadge } from "@/components/ComingSoonBadge";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { format, currency } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: user?.email || "", full_name: user?.name || "", address: "", city: "", country: "", zip: "",
  });

  const shipping = subtotal >= 75 ? 0 : 6.99;
  const total = subtotal + shipping;

  if (items.length === 0) {
    return <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-2xl font-bold text-white">Your cart is empty</h1>
      <Button onClick={() => navigate("/shop")} className="mt-6 rounded-xl bg-[#00F0FF] text-[#08090E] hover:bg-[#33F3FF]" data-testid="checkout-empty-shop">Browse Products</Button>
    </div>;
  }

  const placeOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        email: form.email, full_name: form.full_name, currency,
        origin_url: window.location.origin, user_id: user?.id || null,
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        shipping_address: { address: form.address, city: form.city, country: form.country, zip: form.zip },
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="mb-2 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#00F0FF]"><Lock className="h-3.5 w-3.5" /> Secure Checkout</div>
      <h1 className="font-display text-3xl font-bold text-white">Checkout</h1>

      <form onSubmit={placeOrder} className="mt-8 grid gap-8 lg:grid-cols-[1fr,400px]">
        <div className="space-y-6">
          <section className="dw-card rounded-2xl p-6">
            <h2 className="font-display text-lg font-semibold text-white">Contact & Shipping</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Label className="text-slate-300">Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="checkout-email" placeholder="you@example.com" /></div>
              <div className="sm:col-span-2"><Label className="text-slate-300">Full Name</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="checkout-name" /></div>
              <div className="sm:col-span-2"><Label className="text-slate-300">Address</Label><Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="checkout-address" /></div>
              <div><Label className="text-slate-300">City</Label><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="checkout-city" /></div>
              <div><Label className="text-slate-300">Country</Label><Input required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="checkout-country" /></div>
              <div><Label className="text-slate-300">Postal / ZIP</Label><Input required value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="mt-1.5 border-white/10 bg-white/5" data-testid="checkout-zip" /></div>
            </div>
          </section>

          <section className="dw-card rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-semibold text-white">Payment</h2>
              <span className="rounded-full bg-[#635BFF]/15 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#a9a4ff]">Stripe</span>
            </div>
            <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-4" data-testid="stripe-notice">
              <p className="text-sm text-slate-300">You'll be redirected to Stripe's secure checkout to pay by card. Tax is calculated automatically at checkout.</p>
              <p className="mt-2 font-mono text-xs text-slate-500">Test card: 4242 4242 4242 4242 · any future date · any CVC</p>
            </div>
            <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500"><ShieldCheck className="h-3.5 w-3.5 text-[#00E676]" /> Payments securely processed by Stripe. We never store your card details.</p>
          </section>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="dw-card rounded-2xl p-6">
            <h2 className="font-display text-lg font-semibold text-white">Order Summary</h2>
            <div className="mt-4 space-y-3">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-3">
                  <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-[#0B0D16]"><img src={i.image} alt="" className="h-full w-full object-cover opacity-90" /><span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#00F0FF] text-[10px] font-bold text-[#08090E]">{i.quantity}</span></div>
                  <div className="flex-1"><p className="line-clamp-1 text-sm text-slate-200">{i.name}</p>{i.coming_soon && <ComingSoonBadge className="mt-1 scale-90 origin-left" />}</div>
                  <span className="font-mono text-sm text-slate-200">{format(i.price_usd * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-2 border-t border-white/10 pt-4 text-sm">
              <div className="flex justify-between text-slate-400"><span>Subtotal</span><span className="font-mono text-slate-200">{format(subtotal)}</span></div>
              <div className="flex justify-between text-slate-400"><span>Shipping</span><span className="font-mono text-slate-200">{shipping === 0 ? "FREE" : format(shipping)}</span></div>
              <div className="flex justify-between pt-2 text-base font-bold text-white"><span>Total</span><span className="font-mono text-[#00F0FF]" data-testid="checkout-total">{format(total)}</span></div>
            </div>
            <Button type="submit" disabled={loading} className="mt-5 w-full rounded-xl bg-[#00F0FF] py-6 text-base font-bold text-[#08090E] hover:bg-[#33F3FF]" data-testid="place-order-btn">
              {loading ? "Redirecting…" : <>Pay {format(total)} <ChevronRight className="ml-1 h-4 w-4" /></>}
            </Button>
          </div>
        </aside>
      </form>
    </div>
  );
}
